// @flow

import { observer } from "mobx-react";
import React from "react";
import TimeAgo from "react-timeago";

import TimelineItem from "app/models/TimelineItem";
import Video from "app/models/Video";
import VideoAuthor from "app/components/VideoAuthor/VideoAuthor";
import VideoTime from "app/components/VideoTime/VideoTime";
import Timeline from "app/components/Timeline/Timeline";
import TimelineBars from "app/components/TimelineBars/TimelineBars";
import TimelineDots from "app/components/TimelineDots/TimelineDots";
import VideoFolderMove from "app/components/VideoFolderMove/VideoFolderMove";
import VideoFolderMoveStore from "app/stores/VideoFolderMoveStore";

import ImageHelper from "app/util/ImageHelper";

import playIcon from "./playIcon.png";
import "./VideoCard.scss";

type Props = {
    video: Video,
    items?: ?(TimelineItem[]),
    serverTime: any,
    theme: string,
    withoutUrl?: boolean,
    showTimestamp?: boolean,
    scale?: number,
    playIcon?: boolean,
    created_by?: string,
    timelineThumbnail?: boolean,
    onlyTimeline: ?boolean,
    showUnplublishWarning?: boolean,
    showFolderMove?: boolean,
};

@observer
export default class VideoCard extends React.Component<Props> {
    constructor(props: Props) {
        super(props);
        this.state = {
            opened: false,
        };
    }

    handleToggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ opened: !this.state.opened });
    };

    handleClickOutside = () => {
        this.setState({ opened: false });
    };

    renderFolderMove() {
        if (!this.props.showFolderMove) return null;

        return (
            <button className="FolderList__button" key={this.props.video.uid} onClick={(e) => {
                e.preventDefault()
                VideoFolderMoveStore.startEditing(this.props.video)
            }}>
                <i className="fa fa-folder-open"></i>   
            </button>
        )
    }

    getUserProfileUrl() {
        const authorSlug = this.props.video.author_slug;
        if (!authorSlug) return "";

        return "/u/" + authorSlug;
    }

    getThumbnail = (video: any) => {
        if (this.props.timelineThumbnail && video.timeline_thumbnail) {
            return ImageHelper.makeUrl(video.timeline_thumbnail, "jpg", {
                width: 400
            });
        } else if (video.thumbnail) {
            return ImageHelper.makeUrl(video.thumbnail, "jpg", { width: 400 });
        } else {
            return video.source_thumbnail;
        }
    };

    render() {
        return this.props.onlyTimeline ?
            this.renderOnlyTimeline() : this.renderNormal();
    }

    renderOnlyTimeline() {
        let classes = ["VideoCard", "VideoCard--theme-" + this.props.theme];

        if (!this.props.video.available) {
            classes.push("VideoCard--unavailable");
        }

        if (this.props.showTimestamp) {
            classes.push("VideoCard--extra-height");
        }
        
        const video = this.props.video;
        let url = `/watch?tlv=${video.uid}&r=${video.version}`;

        if (this.props.withoutUrl) {
            url = "#";
            classes.push("VideoCard--without-url");
        }
        
        const thumbnail = this.getThumbnail(video);

        return (
            <a href={url} className={classes.join(" ")}>
                <div className="VideoCard__content">
                    {this.renderContentBody()}
                    {this.renderContentFooter()}
                </div>
                <div className="VideoCard__backgroundContainer">
                    <div className="VideoCard__background" style={{ backgroundImage: `url(${thumbnail})` }}></div>
                    {this.renderBars()}
                    {this.renderBarsTimeline()}
                </div>
            </a>
        );
    }

    renderNormal() {
        let classes = ["VideoCard", "VideoCard--theme-" + this.props.theme];

        if (!this.props.video.available) {
            classes.push("VideoCard--unavailable");
        }

        if (this.props.showTimestamp) {
            classes.push("VideoCard--extra-height");
        }

        const video = this.props.video;
        let url = `/watch?tlv=${video.uid}&r=${video.version}`;

        const thumbnail = this.getThumbnail(video);

        if (this.props.withoutUrl) {
            url = "#";
            classes.push("VideoCard--without-url");
        }

        return (
            <div className={classes.join(" ")}>
                <a href={url} >
                    <div className="VideoCard__backgroundContainer" style={{clear:"both"}}>
                        <div className="VideoCard__background" style={{ backgroundImage: `url(${thumbnail})` }}></div>
                        <div
                            className="VideoCard__thumbnail"
                            style={{ backgroundImage: `url(${thumbnail})` }}
                        >
                            {this.renderPlayIcon()}
                            <VideoTime video={this.props.video} />
                            {this.renderUnavailable()}
                            {this.renderDotsTimeline()}
                        </div>

                        {this.renderDots()}
                        {this.renderBars()}
                        {this.renderBarsTimeline()}
                    </div>

                </a>
                <div className="VideoCard__content">
                    {this.renderContentBody()}
                    {this.renderContentFooter()}
                </div>
                {   
                    VideoFolderMoveStore.isEditing &&
                    VideoFolderMoveStore.video === this.props.video &&
                    <VideoFolderMove video={this.props.video} />
                }
                
            </div>
        );
    }

    renderPlayIcon() {
        if (!this.props.playIcon) return null;

        return <img className="VideoCard__playIcon" src={playIcon} alt="" />;
    }

    renderDotsTimeline() {
        if (this.props.theme !== "default") return null;

        const items = this.props.items || [];
        return (
            <div className="VideoCard__items">
                <Timeline
                    video={this.props.video}
                    items={items}
                    scale={this.props.scale || 0.3}
                />
            </div>
        );
    }

    renderDots() {
        if (this.props.theme !== "default") return null;

        const items = this.props.items || [];
        if (!items.length) return null;

        return <TimelineDots video={this.props.video} items={items} />;
    }

    renderBars() {
        if (this.props.theme !== "alternative") return null;

        const items = this.props.items || [];
        if (!items.length) return null;

        return <TimelineBars video={this.props.video} items={items} />;
    }

    renderBarsTimeline() {
        if (this.props.theme !== "alternative") return null;

        const items = this.props.items || [];
        if (!items.length) return null;

        return (
            <Timeline
                video={this.props.video}
                items={items}
                scale={this.props.scale || 0.3}
            />
        );
    }

    renderContentBody() {
        const result = [];
        const theme = this.props.theme;

        if (theme === "default") {
            result.push(this.renderAuthor());
            result.push(this.renderFolderMove());
            result.push(this.renderContentTitle());
        } else {
            result.push(this.renderContentTitle());
            result.push(this.renderFolderMove());
            result.push(this.renderAuthor());
        }

        return result;
    }

    renderContentFooter() {
        if (!this.props.video.created_by) return null;

        return (
            <footer className="VideoCard__content-footer">
                {this.renderTimestamp()}
                {this.renderNotPublished()}
            </footer>
        );
    }
    
    renderNotPublished() {
        if (!this.props.showUnplublishWarning) return null;

        if (this.props.video.unpublished === "0" ) return null;
        return (
            <span>&nbsp;- unpublished changes</span>
        )
    }

    renderAuthor() {
        const video = this.props.video;

        const authorName =
            video.author_name || video.author_username || undefined;
        const authorImage = video.author_image || undefined;

        return (
            <a key="author" href={this.getUserProfileUrl()}>
                <VideoAuthor name={authorName} image={authorImage} />
            </a>
        );
    }

    renderContentTitle() {
        return (
            <div key="title" className="VideoCard__title">
                {this.props.video.title}
            </div>
        );
    }

    renderUnavailable() {
        if (this.props.video.available || this.props.created_by) return null;

        return (
            <p className="VideoCard__unavailable">
                Media
                <br />
                unavailable
            </p>
        );
    }

    renderTimestamp() {
        if (!this.props.showTimestamp) {
            return null;
        }

        return (
            <span className="VideoCard__timeago">
                <TimeAgo
                    date={this.props.video.updated_at || ""}
                    now={() => this.props.serverTime}
                    title={this.props.video.updated_at}
                />
            </span>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/components/VideoCard/VideoCard.js